import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, DollarSign, Users, TrendingUp, Percent } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ label, value, hint, icon: Icon, tone = "default" }) {
  const t = { default: "text-[#0F172A]", good: "text-[#047857]" }[tone];
  return (
    <div className="p-5 rounded-2xl bg-white border border-[#E9ECEF]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#64748B] font-medium">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className={`mt-1 text-3xl font-semibold font-mono-num ${t}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] text-[#64748B]">{hint}</div>}
    </div>
  );
}

export default function PartnerDashboard() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/partners/${encodeURIComponent(code)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e?.response?.status === 404 ? "code_not_found" : "load_failed"));
  }, [code]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFBFC]">
        <div className="max-w-md w-full p-6 rounded-2xl bg-white border border-[#E9ECEF] text-center">
          <div className="text-lg font-semibold text-[#0F172A] mb-2">Partner code not found</div>
          <p className="text-sm text-[#475569] mb-4">The code <span className="font-mono-num">{code}</span> isn&apos;t active. If this is your code and you think it should be, email <a href="mailto:rsell.ai.real@gmail.com" className="text-[#047857] underline">rsell.ai.real@gmail.com</a>.</p>
          <Link to="/" className="text-[#047857] underline text-sm">Back to rsell ai</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#64748B]">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC] p-4 sm:p-8" data-testid="partner-dashboard">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-semibold">Creator dashboard</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="text-3xl font-semibold text-[#0F172A] font-mono-num">{data.code}</h1>
            {data.name && <span className="text-sm text-[#475569]">· {data.name}</span>}
            {data.platform && <span className="text-xs text-[#64748B]">· {data.platform}{data.handle ? ` (${data.handle})` : ""}</span>}
          </div>
          <p className="text-sm text-[#64748B] mt-1">
            {data.rev_share_pct}% recurring commission — earned every month a referred subscriber stays paying.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat label="Referred signups" value={data.referred_signups.toLocaleString()} icon={Users} testid="p-signups" />
          <Stat label="Conversions" value={data.conversions.toLocaleString()} hint={`${data.conversion_rate_pct}% rate`} tone="good" icon={TrendingUp} />
          <Stat label="Still paying" value={data.still_paying.toLocaleString()} hint="Active subscribers now" tone="good" />
          <Stat label="MRR generated" value={`$${data.mrr_usd.toFixed(2)}`} hint="This month" icon={DollarSign} tone="good" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
          <Stat label="Commission this month" value={`$${data.commission_this_period_usd.toFixed(2)}`} icon={Percent} tone="good" />
          <Stat label="Total earned" value={`$${data.total_earned_usd.toFixed(2)}`} hint="Since your code went live" tone="good" />
        </div>

        <div className="p-5 rounded-2xl bg-white border border-[#E9ECEF]">
          <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-medium mb-1">Your referral link</div>
          <div className="flex items-center gap-2">
            <input readOnly value={`https://rsellai.top/?ref=${data.code}`}
              onFocus={(e) => e.target.select()}
              className="flex-1 px-3 py-2.5 rounded-lg border border-[#E9ECEF] bg-[#FAFBFC] text-sm font-mono-num text-[#0F172A]"
              data-testid="partner-ref-link"
            />
            <button
              onClick={async () => {
                try { await navigator.clipboard.writeText(`https://rsellai.top/?ref=${data.code}`); }
                catch { /* noop */ }
              }}
              className="px-4 py-2.5 rounded-lg bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B]"
            >Copy</button>
          </div>
          <p className="text-[11px] text-[#64748B] mt-2 leading-relaxed">
            Anyone visiting this link within 90 days will be attributed to you — even if they close the tab and come back later.
            Referred subscribers get <strong className="text-[#0F172A]">2× scans their first month</strong> as a thank-you (once per person, ever).
          </p>
        </div>

        <div className="text-[11px] text-[#94A3B8] mt-6 text-center">
          Dashboard updates every ~5 min. Payouts monthly via ACH / PayPal — you&apos;ll be contacted for details after your first conversion.
        </div>
        <div className="text-[11px] text-[#94A3B8] mt-1 text-center">
          Bookmark this URL — the code in the link is your access token. Do not share it publicly.
        </div>
        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-[#64748B] hover:text-[#0F172A] underline">← back to rsell ai</Link>
        </div>
      </div>
    </div>
  );
}
